import * as Sentry from '@sentry/react';
import { QueryClient } from '@tanstack/query-core';
import { ERR_BEARER_TOKEN_NOT_SET, CLIENT_RETRY_COUNT, ERR_LOGIN_REQ } from '@/consts/client';
import env from '@/env';
import { FetchError } from './FetchError';

export { FetchError };

export const getQueryClientCanRetry = (failureCount: number, error: Error) => {
  // eslint-disable-next-line no-console
  console.warn(`Query failed ${failureCount} times`, error);

  if (error.message === ERR_LOGIN_REQ) return false;
  if (error.message === ERR_BEARER_TOKEN_NOT_SET) return true;

  if (error instanceof FetchError && error.status >= 400 && error.status < 500) {
    return false;
  }

  /* this check is because sometimes our tests won't fail correctly
  because it will retry 3 times which takes too long so they fail
  doing this so we don't retry in our tests as it's unnecessary  */
  if (env.VITE_NODE_ENV === 'test') {
    return false;
  }

  /* istanbul ignore next */
  return failureCount <= CLIENT_RETRY_COUNT;
};

export const getQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 3600000, // 1 hour
        retry: getQueryClientCanRetry,
        throwOnError: true,
      },
    },
  });

export const getFetchClient = (getToken: () => Promise<string>) => async (input: RequestInfo, init?: RequestInit) => {
  // Wrapping the fetch within Sentry startSpan to allow Sentry to capture how long API calls take
  // https://docs.sentry.io/platforms/javascript/performance/instrumentation/custom-instrumentation/#start-span
  const fetchWithToken = (token: string) =>
    Sentry.startSpan({ op: 'fetch', name: `Fetch: ${input}` }, async span => {
      const response = await window.fetch(`${env.VITE_PARACHUTE_REST_API_ENDPOINT}${input}`, {
        ...init,
        headers: {
          ...init?.headers,
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        span?.setStatus('unknown_error');
        throw new FetchError(response.status, String(input));
      }

      span?.setStatus('ok');

      // for requests that don't return a body, response.json() gives errors
      // this check is to confirm there is a request body and then parse it
      // else just return the response
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json();
      }
      return response;
    });

  const token = await getToken();
  try {
    return await fetchWithToken(token);
  } catch (error) {
    if (error instanceof FetchError && error.status === 401) {
      const freshToken = await getToken();
      return fetchWithToken(freshToken);
    }
    throw error;
  }
};
